import { CircularProgress, Button as UIButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import Home6LineIcon from "remixicon-react/Home6LineIcon";
import Download2LineIcon from "remixicon-react/Download2LineIcon";
import AddIcon from "@mui/icons-material/Add";
import { useCallback, useEffect, useState } from "react";
import { useSnackbar } from "$utils/useSnackbar";
import { useLocation } from "react-router-dom";
import { Text, InfoContainer, Table } from "$components";
import { theme } from "$utils/theme/bataillog";
import { ACTIVITIES_LABELS } from "$utils/typeConstants";

import { useActivitiesQuery } from "$hooks/useActivitiesQuery";
import { CompanyType } from "$types/CompanyType";

import JImportModal from "$components/JImportModal/JImportModal";
import { snackbarSuccessOptions } from "$utils/SnackbarOptions";
import { ImportType } from "$types/import/ImportType";
import EntrepriseForm from "../EntrepriseForm";
import useCompanyForm from "$hooks/useCompanyForm";
import { usePopin } from "$components/Popin/usePopin";
import { CompanyServices } from "$services";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";

type Props = {
  company: any;
  onImportFinish?: () => void;
  redirect: (url: string) => void;
  fetchCompany: any; // TODO will be remove
  selectedTab: CompanyType;
};

const InfoHeaderRow = styled("div")({
  display: "flex",
  alignItems: "center",
  "& h3": {
    margin: "0 8px",
  },
});

const FullWidthInfoContainer = styled(InfoContainer)({
  width: "100%",
  marginTop: theme.spacing(2),
  "& .MuiButton-root": {
    color: "#000000 !important",
    backgroundColor: "transparent !important",
    boxShadow: "none !important",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "#f5f5f5 !important",
      color: "#000000 !important",
      boxShadow: "none !important",
    },
    "&:disabled": {
      color: "#cccccc !important",
      backgroundColor: "transparent !important",
    },
  },
});

const NoPaddingTable = styled(Table)({
  padding: "0 !important",
});

function AgenciesTable({
  company,
  onImportFinish,
  redirect,
  fetchCompany,
  selectedTab,
}: Props) {
  const { data: activities = [], isLoading: activitiesLoader } =
    useActivitiesQuery();
  const [openSnackbar] = useSnackbar(snackbarSuccessOptions);
  const [showImportModal, setShowImportModal] = useState(false);
  const location = useLocation();

  const { Popin, openPopin, togglePopin, PopinProps } = usePopin();
  // @todo amir: why it's useless ? we still need it to make the component reusable !
  // TODO will remove this with the form update, it's useless
  const { handleSubmit, error, submitting } = useCompanyForm(fetchCompany);
  const [companies, setCompanies] = useState([]);
  const [companiesLoader, setIsLoading] = useState(false);

  const isCarrier = company?.companyTypes
    .filter((t) => t === selectedTab)
    .includes(CompanyType.CARRIER);

  const isClient = company?.companyTypes
    .filter((t) => t === selectedTab)
    .includes(CompanyType.CLIENT);

  const redirectToAgency = (
    companyId: string,
    companyType: CompanyType.AGENCY | CompanyType.CONTRIBUTOR
  ) => {
    const { pathname } = location;
    const url = `${pathname}/${
      companyType === CompanyType.AGENCY ? "agence-transporteur" : "intervenant"
    }/${companyId}`;
    redirect(url);
  };

  const getCell = (company: any, value) => ({
    value,
    onClickAction: () => redirectToAgency(company._id, CompanyType.AGENCY),
    isDisable: !company.isActive,
  });

  const fecthCompanies = useCallback(async () => {
    if (company?._id) {
      const filters = {
        agencyOf: company?._id,
      };
      let companies;
      companies = await queryClient.getQueryData([
        CacheInvalidationAction.GET_COMPANIES,
        filters,
      ]);
      if (!companies) {
        companies = await queryClient.fetchQuery({
          queryKey: [CacheInvalidationAction.GET_COMPANIES, filters],
          queryFn: () => CompanyServices.getCompanies(filters),
          gcTime: 60 * 1000,
        });
        companies = companies.data;
        await queryClient.setQueryData(
          [CacheInvalidationAction.GET_COMPANIES, filters],
          companies
        );
      }
      setCompanies(companies);
    }
  }, [setCompanies, company?._id]);

  useEffect(() => {
    setIsLoading(true);
    fecthCompanies();
    setIsLoading(false);
  }, [fecthCompanies]);

  const bodyTable = companies
    ?.filter((c) => c.agencyOf === company._id)
    .map((c) => [
      getCell(c, <Text type="important">{c.name}</Text>),
      getCell(
        c,
        activitiesLoader ? (
          <CircularProgress size={24} />
        ) : (
          <Text>
            {c.activities
              .map(
                (aId) =>
                  ACTIVITIES_LABELS[
                    activities?.find((a) => a._id === aId)?.name
                  ]
              )
              .join(", ")}
          </Text>
        )
      ),
      getCell(c, <Text>{c.adress}</Text>),
      getCell(c, <Text>{c.postalCode}</Text>),
      getCell(c, <Text>{c.city}</Text>),
      getCell(c, <Text>{c.email || "N/A"}</Text>),
    ]);

  return (
    <>
      <FullWidthInfoContainer
        isLoading={companiesLoader}
        isToggleable
        leftHeader={
          <InfoHeaderRow>
            <Home6LineIcon />
            <Text type="h3">Agence(s) transporteur</Text>
          </InfoHeaderRow>
        }
        rightHeader={
          <>
            <UIButton
              onClick={openPopin}
              sx={
                company?.isActive === false
                  ? {
                      color: "rgba(0, 0, 0, 0.37) !important",
                      pointerEvents: "none",
                    }
                  : undefined
              }
              style={{ textTransform: "none" }}
              startIcon={<AddIcon />}
            >
              Ajouter une agence
            </UIButton>
            <UIButton
              onClick={() => setShowImportModal(true)}
              sx={
                company?.isActive === false
                  ? {
                      color: "rgba(0, 0, 0, 0.37) !important",
                      pointerEvents: "none",
                    }
                  : undefined
              }
              style={{ textTransform: "none" }}
            >
              <Download2LineIcon />
              <Text>Importer liste agences</Text>
            </UIButton>
          </>
        }
      >
        <NoPaddingTable
          bodyMissingLabelText="Cette entreprise n'a pas encore d'agences transporteur"
          rowsPerPage={8}
          thead={[
            { id: "name", label: "agence transporteur" },
            { id: "activities", label: "activité(s)" },
            { id: "address", label: "adresse" },
            { id: "postalCode", label: "code postal" },
            { id: "city", label: "ville" },
            { id: "email", label: "e-mail" },
          ]}
          bodyData={bodyTable}
          usePaper={false}
        />

        <JImportModal
          dataName="agences"
          contextId={company?._id}
          onImportFinish={onImportFinish}
          openSnackbar={openSnackbar}
          setShowImportModal={setShowImportModal}
          showImportModal={showImportModal}
          type={ImportType.AGENCY_MULTIPLE}
        />
      </FullWidthInfoContainer>
      <Popin {...PopinProps}>
        <EntrepriseForm
          onSubmit={handleSubmit}
          onReset={togglePopin}
          companyError={error}
          isSubmitting={submitting}
          readOnlyFields={["agencyOf", "companyTypes", "contributorOf"]}
          defaultValues={{
            companyTypes: isCarrier
              ? [CompanyType.AGENCY]
              : [CompanyType.CONTRIBUTOR],
            ...(isCarrier && { agencyOf: company }),
            ...(isClient && { contributorOf: [company] }), // TODO check this but i'm pretty sure it can never be use
          }}
        />
      </Popin>
    </>
  );
}

export default AgenciesTable;
